/**
 * Development-mode logger utility
 *
 * This logger only outputs in development mode to help with debugging
 * while keeping production builds clean of console statements.
 */

const isDevelopment = process.env.NODE_ENV === 'development';

interface LoggerOptions {
  enabled?: boolean;
  prefix?: string;
}

class Logger {
  private enabled: boolean;
  private prefix: string;

  constructor(options: LoggerOptions = {}) {
    this.enabled = options.enabled ?? isDevelopment;
    this.prefix = options.prefix ?? '';
  }

  private formatMessage(level: string, ...args: unknown[]): unknown[] {
    if (this.prefix) {
      return [`[${this.prefix}] [${level}]`, ...args];
    }
    return [`[${level}]`, ...args];
  }

  log(...args: unknown[]): void {
    if (this.enabled) {
      console.log(...this.formatMessage('LOG', ...args));
    }
  }

  info(...args: unknown[]): void {
    if (this.enabled) {
      console.info(...this.formatMessage('INFO', ...args));
    }
  }

  debug(...args: unknown[]): void {
    if (this.enabled) {
      console.log(...this.formatMessage('DEBUG', ...args));
    }
  }

  warn(...args: unknown[]): void {
    if (this.enabled) {
      console.warn(...this.formatMessage('WARN', ...args));
    }
  }

  error(...args: unknown[]): void {
    if (this.enabled) {
      console.error(...this.formatMessage('ERROR', ...args));
    }
  }

  group(label: string): void {
    if (this.enabled) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.enabled) {
      console.groupEnd();
    }
  }

  table(data: unknown): void {
    if (this.enabled) {
      console.table(data);
    }
  }

  time(label: string): void {
    if (this.enabled) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.enabled) {
      console.timeEnd(label);
    }
  }
}

// Create default logger instance
const logger = new Logger();

// Export both the class and default instance
export { Logger, logger };

// Export individual methods for convenience
export const log = logger.log.bind(logger);
export const info = logger.info.bind(logger);
export const debug = logger.debug.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);

// Factory function to create a logger with a specific prefix
export function createLogger(prefix: string): Logger {
  return new Logger({ prefix });
}

export default logger;